import { useQuery } from '@tanstack/react-query'
import { Link } from '@tanstack/react-router'
import { BookOpen, Sparkles } from 'lucide-react'

import { keys } from '@/api/keys'
import { listStories, type StoryRow } from '@/api/stories'
import { playIntentProps } from '@/components/StoryPlayer'
import { Button } from '@/components/ui/button'
import { cn } from '@/components/ui/utils'
import { storyLengthLine } from '@/features/story/StoryList'
import { StoryThumb } from '@/features/story/StoryThumb'
import { StoryModelLabel } from '@/features/story/StoryModelLabel'

/**
 * The Library's **Stories** shelf (`/library?kind=story`): every made story as a picture with its
 * length in the corner, the way a video library shows its films — its first part's stage with the
 * cast in their opening places (`thumb_url`), "4:13", the parts of a longer story, the title. A
 * card opens the player. The search box of the filter bar narrows by title or idea, here, without
 * a request (a studio holds tens of stories, not thousands).
 *
 * Only `ready` stories: one still being made, or one that failed, has nothing to watch yet and
 * lives on the Stories page.
 */

export interface StoryShelfProps {
  /** The filter bar's search (the route's `q`). */
  q?: string
  view?: 'grid' | 'list'
}

/** Stories whose title or idea holds every word of `q` (case-insensitive); all of them for none. */
export function filterStories<T extends Pick<StoryRow, 'title' | 'prompt'>>(items: readonly T[], q: string | undefined): T[] {
  const words = (q ?? '').toLowerCase().split(/\s+/).filter(Boolean)
  if (!words.length) return [...items]
  return items.filter((s) => {
    const text = `${s.title} ${s.prompt}`.toLowerCase()
    return words.every((w) => text.includes(w))
  })
}

export function StoryShelf({ q, view = 'grid' }: StoryShelfProps) {
  // `always`, like the assets query: the studio talks to its own server.
  const query = useQuery({ queryKey: keys.stories.list({ status: 'ready' }), queryFn: ({ signal }) => listStories('ready', signal), networkMode: 'always' })
  const all = query.data?.items ?? []
  const items = filterStories(all, q)

  if (query.isError) {
    return (
      <div className="card flex flex-col items-center gap-3 px-6 py-16 text-center" role="alert">
        <h2 className="text-lg font-semibold text-ink">The stories could not load</h2>
        <Button variant="primary" onClick={() => void query.refetch()}>
          Try again
        </Button>
      </div>
    )
  }
  if (query.isPending) {
    return (
      <div className="grid grid-cols-[repeat(auto-fill,minmax(240px,1fr))] gap-4" aria-hidden data-testid="story-shelf-loading">
        {Array.from({ length: 6 }, (_, i) => (
          <div key={i} className="card aspect-[4/3] animate-pulse bg-paper-2" />
        ))}
      </div>
    )
  }
  if (!items.length) {
    return (
      <div className="card flex flex-col items-center gap-3 px-6 py-16 text-center" data-testid="story-shelf-empty">
        <span className="inline-flex size-14 items-center justify-center rounded-2xl bg-paper-2 text-ink-2">
          <BookOpen className="size-7" aria-hidden />
        </span>
        <h2 className="text-lg font-semibold text-ink">{all.length ? 'No story matches this search' : 'No stories yet'}</h2>
        {all.length ? null : (
          <Button asChild variant="primary">
            <Link to="/stories/new">
              <Sparkles aria-hidden />
              Make a story
            </Link>
          </Button>
        )}
      </div>
    )
  }

  return (
    <div className="flex flex-col gap-3">
      <p className="text-xs text-ink-3" aria-live="polite">
        {items.length} stor{items.length === 1 ? 'y' : 'ies'}
      </p>
      <ul
        role="list"
        data-testid="story-shelf"
        className={cn(view === 'list' ? 'flex flex-col gap-2' : 'grid grid-cols-[repeat(auto-fill,minmax(240px,1fr))] gap-4')}
      >
        {items.map((story) => (
          // A pointer resting on a card (or focus inside it) starts fetching the story (`playIntentProps`).
          <li key={story.uid} {...playIntentProps(story.uid)}>
            <Link
              to="/stories/$uid"
              params={{ uid: story.uid }}
              search={{ step: 'play' }}
              aria-label={`Play “${story.title}”${storyLengthLine(story) ? `, ${storyLengthLine(story)}` : ''}`}
              data-testid="story-shelf-card"
              aria-describedby={`story-model-${story.uid}`}
              className={cn(
                'group card flex overflow-hidden transition-shadow hover:shadow-pop focus-visible:focus-ring',
                view === 'list' ? 'flex-row items-center' : 'h-full flex-col',
              )}
            >
              <StoryThumb story={story} playable showParts={view !== 'list'} className={view === 'list' ? 'w-40 flex-none sm:w-48' : undefined} />
              <span className={cn('flex min-w-0 flex-col gap-0.5', view === 'list' ? 'px-4 py-2' : 'p-3')}>
                <span className="line-clamp-2 font-semibold text-ink" title={story.title}>
                  {story.title}
                </span>
                {storyLengthLine(story) ? <span className="text-xs text-ink-3">{storyLengthLine(story)}</span> : null}
                <StoryModelLabel story={story} id={`story-model-${story.uid}`} />
                {view === 'list' ? <span className="line-clamp-1 text-sm text-ink-2">{story.prompt}</span> : null}
              </span>
            </Link>
          </li>
        ))}
      </ul>
    </div>
  )
}
